import React from "react";
import { createPortal } from "react-dom";
import { getReactPortalRoot } from "../utils/reactPortalRoot.js";
import {
  formatDispatchSaveImportDate,
  formatDispatchSaveImportDateList,
  formatDispatchSaveImportTimestamp,
} from "../utils/dispatchSavesImport.js";
import { buttonToneClasses, joinClasses } from "./resultsPrimitives/shared.js";

const CONFIRM_BUTTON_CLASS_NAME = joinClasses([
  "inline-flex min-h-9 items-center justify-center gap-1.5 rounded-full border px-3.5 py-2 text-[12px] font-semibold transition disabled:cursor-not-allowed disabled:opacity-60",
  buttonToneClasses.accent,
]);
const CANCEL_BUTTON_CLASS_NAME = joinClasses([
  "inline-flex min-h-9 items-center justify-center gap-1.5 rounded-full border px-3.5 py-2 text-[12px] font-semibold transition disabled:cursor-not-allowed disabled:opacity-60",
  buttonToneClasses.ghost,
]);

function buildDefaultSelection(candidates = []) {
  return candidates.reduce((selection, candidate) => {
    if (candidate?.import_key) {
      selection[candidate.import_key] = false;
    }
    return selection;
  }, {});
}

export default function DataManagerDispatchSavesImportDialog({
  candidates = [],
  errorMessage = "",
  isOpen = false,
  isSubmitting = false,
  onCancel,
  onConfirm,
}) {
  const [selectionByKey, setSelectionByKey] = React.useState(() => buildDefaultSelection(candidates));

  React.useEffect(() => {
    if (isOpen) {
      setSelectionByKey(buildDefaultSelection(candidates));
    }
  }, [candidates, isOpen]);

  if (!isOpen) {
    return null;
  }

  const selectedCount = candidates.filter((candidate) => selectionByKey[candidate.import_key]).length;
  const alreadyImportedCount = candidates.filter((candidate) => candidate.already_imported === true).length;
  const dispatchDateLabels = formatDispatchSaveImportDateList(
    candidates.map((candidate) => candidate.dispatch_date).filter(Boolean),
  );
  const alreadyImportedMessage = dispatchDateLabels
    ? `Certaines sauvegardes de ce fichier sont déjà présentes localement pour la journée ${dispatchDateLabels}. Elles peuvent être réimportées comme nouvelles copies locales.`
    : "Certaines sauvegardes de ce fichier sont déjà présentes localement. Elles peuvent être réimportées comme nouvelles copies locales.";

  return createPortal(
    <div data-react-confirmation-modal="true" className="fixed inset-0 z-modal isolate font-sans" role="presentation">
      <div
        className="absolute inset-0 bg-slate-950/45 px-4 py-5 lg:px-6"
        onMouseDown={(event) => {
          if (event.target === event.currentTarget && !isSubmitting) {
            onCancel?.();
          }
        }}
      >
        <div className="flex min-h-full items-center justify-center">
          <section
            aria-labelledby="dispatch-saves-import-title"
            aria-modal="true"
            className="relative max-h-[calc(100dvh-2.5rem)] w-full max-w-[min(42rem,calc(100vw-1.5rem))] overflow-hidden rounded-overlay border border-line-soft bg-surface text-text shadow-floating"
            role="dialog"
          >
            <div className="h-1.5 w-full bg-accent" />
            <div className="px-4 py-4 sm:px-5">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[0.64rem] font-semibold uppercase tracking-[0.18em] text-accent-strong">Import sauvegardes dispatch</p>
                  <h2 id="dispatch-saves-import-title" className="mt-2 text-[1.2rem] font-semibold tracking-[-0.02em] text-slate-950">
                    Quelles sauvegardes importer ?
                  </h2>
                  <p className="mt-2 text-[0.88rem] leading-5 text-slate-600">
                    Choisissez les sauvegardes à ajouter à votre dispatch local. Elles seront rattachées à leur journée de dispatch, pas à la date de sauvegarde. Les sauvegardes déjà importées ne seront pas écrasées.
                  </p>
                </div>
                <button
                  type="button"
                  className={CANCEL_BUTTON_CLASS_NAME}
                  disabled={isSubmitting}
                  onClick={onCancel}
                >
                  Fermer
                </button>
              </div>

              {errorMessage || alreadyImportedCount > 0 ? (
                <p className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-[0.82rem] text-amber-900">
                  {errorMessage || alreadyImportedMessage}
                </p>
              ) : null}

              <div className="mt-4 grid max-h-[24rem] gap-2 overflow-y-auto">
                {candidates.map((candidate) => {
                  const importKey = candidate.import_key;
                  const isAlreadyImported = candidate.already_imported === true;
                  const isSelected = Boolean(selectionByKey[importKey]);
                  const dispatchDateLabel = formatDispatchSaveImportDate(candidate.dispatch_date);
                  const sourceSavedAtLabel = formatDispatchSaveImportTimestamp(candidate.source_saved_at);

                  return (
                    <label
                      key={importKey}
                      className={[
                        "flex cursor-pointer items-start gap-3 rounded-xl border px-3 py-2.5",
                        isSelected
                          ? "border-slate-400 bg-slate-100"
                          : isAlreadyImported
                            ? "border-amber-100 bg-amber-50/40"
                            : "border-black/6 bg-white",
                      ].join(" ")}
                    >
                      <input
                        type="checkbox"
                        className="mt-1"
                        checked={isSelected}
                        disabled={isSubmitting}
                        onChange={(event) => {
                          setSelectionByKey((currentSelection) => ({
                            ...currentSelection,
                            [importKey]: event.target.checked,
                          }));
                        }}
                      />
                      <span className="min-w-0 flex-1">
                        <span className="flex flex-wrap items-center gap-2">
                          <span className="text-[13px] font-semibold text-slate-950">
                            {candidate.label || "Sauvegarde dispatch"}
                          </span>
                          {isAlreadyImported ? (
                            <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-amber-800">
                              Déjà importée
                            </span>
                          ) : null}
                        </span>
                        <span className="mt-1 block text-[12px] leading-5 text-slate-600">
                          {dispatchDateLabel ? `Journée dispatch ${dispatchDateLabel}` : "Journée dispatch inconnue"}
                          {" · "}
                          {sourceSavedAtLabel ? `Sauvegardée le ${sourceSavedAtLabel}` : "Date de sauvegarde inconnue"}
                          {" · "}
                          {candidate.assigned_orders}/{candidate.total_orders} commandes
                          {candidate.total_trips ? ` · ${candidate.total_trips} tournée${candidate.total_trips > 1 ? "s" : ""}` : ""}
                        </span>
                      </span>
                    </label>
                  );
                })}
              </div>

              <div className="mt-4 flex flex-wrap justify-end gap-2">
                <button type="button" className={CANCEL_BUTTON_CLASS_NAME} disabled={isSubmitting} onClick={onCancel}>
                  Annuler
                </button>
                <button
                  type="button"
                  className={CONFIRM_BUTTON_CLASS_NAME}
                  disabled={isSubmitting || selectedCount === 0}
                  onClick={() => {
                    const selectedImportKeys = candidates
                      .filter((candidate) => selectionByKey[candidate.import_key])
                      .map((candidate) => candidate.import_key);
                    onConfirm?.(selectedImportKeys);
                  }}
                >
                  {isSubmitting ? "Import..." : `Importer ${selectedCount} sauvegarde${selectedCount > 1 ? "s" : ""}`}
                </button>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>,
    getReactPortalRoot(),
  );
}
